/*----------------------------------------------------------------------+
|    Title:	GeomElement.ts                                              |
|    A port of the software Geometry Applet by                          |
|    Author:    David E. Joyce                                          |
|        Department of Mathematics and Computer Science                 |
|        Clark University                                               |
|        Worcester, MA 01610-1477                                       |
|        U.S.A.                                                         |
|                                                                       |
|        http://aleph0.clarku.edu/~djoyce/home.html                     |
|        djoyce@clarku.edu                                              |
|                                                                       |
|    Date:    February, 1996.   Version 2.0.0 May, 1997.                |
|    TypeScript Port: 2019, Nelson Brown, brownnrl@gmail.com            |
|                           https://www.nelsonbrown.net/                |
+----------------------------------------------------------------------*/

import {PointElement} from "./point/PointElement"
import {SlateCanvas} from "../Slate";

export enum Align {
    CENTRAL,
    LEFT,
    RIGHT,
    ABOVE,
    BELOW
}

export abstract class GeomElement {

    constructor() {
    }

    protected _name: string;
    // True for elements that are references to existing elements (e.g.,
    // point;first returns P[0], point;vertex returns polygon.V[n-1]).
    // Preexisting elements are skipped during rotate/translate to avoid
    // double-movement. Matches Java's preexists[] array in Slate.java.
    public preexists: boolean = false;
    // All colors default to null (transparent) — matching Java's Element.java.
    // Named elements get their colors assigned by init() in index.ts, which
    // computes type-appropriate defaults. Internal/intermediate elements
    // created inside constructions stay null (invisible) unless explicitly set.
    private _nameColor: string = null;
    protected _vertexColor : string = null;
    private _edgeColor : string = null;
    private _faceColor : string = null;

    // Highlight defaults pick high-contrast values that read on the typical
    // cream / white backgrounds the library is used against. The previous
    // edge/vertex default of white was invisible on the Elements site's
    // theorem-box background (HSB 35,19,100 ≈ #FFE9CD). Web "gold"
    // (#FFD700) is brighter / more saturated than Material amber and
    // still reads against both cream and white.
    private _nameHighlightColor   : string = '#FFD700';
    private _vertexHighlightColor : string = '#FFD700';
    private _edgeHighlightColor   : string = '#FFD700';
    private _faceHighlightColor   : string = '#FFD700';

    // Default matches Java: Font("TimesRoman", Font.ITALIC, 18)
    protected static _fontSize : number = 18;
    protected static _fontName : string = "Times New Roman";

    // Style scale (#71): when the figure is fit-scaled down to a narrow
    // display (view scale F < 1), decorations — label font, vertex dots,
    // line widths — are multiplied by 1/F so they keep a constant on-screen
    // size and stay readable while the geometry shrinks. Slate.drawElements
    // sets it from the view scale before each frame; 1 (the default, and
    // F=1 desktop/headless) leaves rendering bit-for-bit unchanged.
    static styleScale : number = 1;

    static setFont(fontName: string, fontSize: number): void {
        GeomElement._fontName = fontName;
        GeomElement._fontSize = fontSize;
    }

    // The label font, scaled by the current styleScale.
    static fontString(): string {
        return `italic ${GeomElement._fontSize * GeomElement.styleScale}px ${GeomElement._fontName}`;
    }

    private _draggable : boolean;
    private _dimension : number;
    private _align : Align;

    private _shouldHighlight : boolean = false;
    // Per-element visibility flag honoured by every draw method. Default
    // true preserves existing consumer behaviour; flipping false makes
    // drawFace / drawEdge / drawVertex / drawName early-return so the
    // element disappears without being removed from _elements (so
    // dependents that read its coordinates still work). Used by
    // Slate.setVisibleNames + the slideshow Presentation overlay.
    private _visible : boolean = true;
    // One step above shouldHighlight: when the slideshow caption's
    // {NAME} ref is hovered or clicked, the matching element flips to
    // emphasized. drawEdge / drawVertex paint with the highlight colour
    // at an even thicker stroke / larger dot so the element pops out
    // from any other already-highlighted ones on the slide. Released on
    // mouseleave (or click-to-toggle for sticky).
    // Emphasis amount in [0, 1]. Boolean emphasized is a thin shim
    // that toggles between 0 and 1; the SlateAnimator fades the
    // amount smoothly from 1 → 0 after an animation finishes so the
    // visible "shrink back to highlight thickness" feels less jarring
    // when the underlying element is also slide-highlighted (e.g.
    // ABC polygon edges over the highlighted AB / AC / BC).
    private _emphasisAmount : number = 0;
    // Animation progress for the next slide-transition render. 1 means
    // "fully drawn" (the default and the existing behaviour). Values in
    // [0, 1) tell per-type draw methods to render a partial geometry —
    // a half-traced line, a 30° sweep of an arc, the first edge of a
    // polygon plus a fraction of the next. Driven by Animation
    // subclasses through SlateAnimator during slide transitions; flips
    // back to 1 on each animation's finalise() so the canvas returns
    // to the fully-rendered state when the transition completes or is
    // cancelled.
    private _drawProgress : number = 1;
    protected _pixelTolerance : number = 50;

    protected _getTextMetrics(ctx: CanvasRenderingContext2D, txt: string) : [number, number] {
        let textMetrics = ctx.measureText(txt);
        let w = textMetrics.width;
        // Use actual font metrics for height; fall back to ascent-based estimate
        let h = (textMetrics.actualBoundingBoxAscent != null)
            ? textMetrics.actualBoundingBoxAscent + textMetrics.actualBoundingBoxDescent
            : 16;
        return [w, h];
    }

    drawString(ix : number, iy : number,  c: SlateCanvas, overrideAlign? : Align) {
        let ctx = c.getContext("2d") as CanvasRenderingContext2D;
        if(this._nameColor == null) return;
        ctx.font = GeomElement.fontString();
        ctx.fillStyle = this._nameColor;
        let [w, h] = this._getTextMetrics(ctx, this._name);

        switch (overrideAlign || this._align) {
            case Align.LEFT:
                ix = ix - w - 6;
                iy = iy + h/2 - 4;
                ctx.fillText(this._name, ix, iy);
                return;
            case Align.RIGHT:
                ix += 2;
                iy += h/2 - 4;
                ctx.fillText(this._name, ix, iy);
                return;
            case Align.ABOVE:
                ix -= w/2;
                iy += -h/2 + 4;
                ctx.fillText(this._name, ix, iy);
                return;
            case Align.BELOW:
                ix -= w/2;
                iy += h/2 + 6;
                ctx.fillText(this._name, ix, iy);
                return;
        }
        // compute (dx,dy) coordinates relative to center of canvas
        // and normalized
        let cw = c.width;
        let ch = c.height;
        let dx = (ix - cw/2) * ch;
        let dy = (iy - ch/2) * cw;
        if (dy > dx) {
            if (dy >= -dx)	// put name below
            {
                ix -= w/2;
                iy += h/2 + 6;
                ctx.fillText(this._name, ix, iy);
            }
            else 		// put name left
            {
                ix = ix - w - 6;
                iy += h/2 - 4;
                ctx.fillText(this._name, ix, iy);
            }
        }
        else {
            if (dy >= -dx) {	// put name right
                    ix += 2;
                    iy += h/2 - 4;
                    ctx.fillText(this._name, ix, iy);
            } else { 		// put name above
                ix -= w/2;
                iy += -h/2 + 4;
                ctx.fillText(this._name, ix, iy);
            }
        }
    }

    public get name() {
        return this._name;
    }

    public set name(n : string) {
        this._name = n;
    }

    public abstract update() : void;
    public reset() { this.update(); }
    public defined() : boolean { return false; }
    public drag(tox: number, toy: number) : boolean { return false; }
    // drag returns true when the element is actually dragged
    public abstract translate(dx: number, dy: number) : void;
    public abstract rotate(pivot : PointElement, ac : number, as: number) : void;
    public abstract drawName(c: SlateCanvas) : void;
    public abstract drawFace(c: SlateCanvas) : void;
    public abstract drawEdge(c: SlateCanvas) : void;
    public abstract drawVertex(c: SlateCanvas) : void;

    set nameColor(value: string) {
        this._nameColor = value;
    }

    set edgeColor(value: string) {
        this._edgeColor = value;
    }

    set faceColor(value: string) {
        this._faceColor = value;
    }

    set nameHighlightColor(value: string) {
        this._nameHighlightColor = value;
    }

    set vertexHighlightColor(value: string) {
        this._vertexHighlightColor = value;
    }

    set edgeHighlightColor(value: string) {
        this._edgeHighlightColor = value;
    }

    set faceHighlightColor(value: string) {
        this._faceHighlightColor = value;
    }

    set draggable(value: boolean) {
        this._draggable = value;
    }

    set dimension(value: number) {
        this._dimension = value;
    }

    set align(value: Align) {
        this._align = value;
    }

    set shouldHighlight(value: boolean) {
        this._shouldHighlight = value;
    }

    set visible(value: boolean) {
        this._visible = value;
    }

    set emphasized(value: boolean) {
        this._emphasisAmount = value ? 1 : 0;
    }

    set emphasisAmount(value: number) {
        this._emphasisAmount = value < 0 ? 0 : (value > 1 ? 1 : value);
    }

    set drawProgress(value: number) {
        // Clamp to [0, 1] — out-of-range values would push the partial
        // geometry past either endpoint, which is never what callers
        // mean. Animations tick this in [0, 1] and finalise to 1.
        this._drawProgress = value < 0 ? 0 : (value > 1 ? 1 : value);
    }

    get nameColor(): string {
        return this._nameColor;
    }

    get vertexColor(): string {
        return this._vertexColor;
    }

    set vertexColor(c : string) {
        this._vertexColor = c;
    }

    get edgeColor(): string {
        return this._edgeColor;
    }

    get faceColor(): string {
        return this._faceColor;
    }

    get nameHighlightColor(): string {
        return this._nameHighlightColor;
    }

    get vertexHighlightColor(): string {
        return this._vertexHighlightColor;
    }

    get edgeHighlightColor(): string {
        return this._edgeHighlightColor;
    }

    get faceHighlightColor(): string {
        return this._faceHighlightColor;
    }

    get draggable(): boolean {
        return this._draggable;
    }

    get dimension(): number {
        return this._dimension;
    }

    get align(): Align {
        return this._align;
    }

    get shouldHighlight(): boolean {
        return this._shouldHighlight;
    }

    get visible(): boolean {
        return this._visible;
    }

    get emphasized(): boolean {
        return this._emphasisAmount > 0;
    }

    get emphasisAmount(): number {
        return this._emphasisAmount;
    }

    get drawProgress(): number {
        return this._drawProgress;
    }
}
